<template>
  <div class="p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">{{ $t('Agent Assignments Management') }}</h1>
      <button
        @click="openCreateModal"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
        </svg>
        {{ $t('New Assignment') }}
      </button>
    </div>

    <!-- Agents Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Total Agents') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ assignments.length }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Active Assignments') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ activeCount }}</p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100 text-purple-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Total Couriers Assigned') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ totalCouriers }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Assignments List -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Agent') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Couriers') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Tracking Statuses') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Countries') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Status') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Created') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{{ $t('Actions') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="7" class="px-4 py-8 text-center">
                <div class="flex justify-center items-center">
                  <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-2">{{ $t('Loading...') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="assignments.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                {{ $t('No assignments found') }}
              </td>
            </tr>
            <tr
              v-else
              v-for="assignment in assignments"
              :key="assignment._id"
              class="hover:bg-gray-50"
            >
              <td class="px-4 py-3">
                <div class="flex items-center">
                  <div class="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
                    {{ getInitials(getAgentName(assignment)) }}
                  </div>
                  <div class="ml-3">
                    <div class="text-sm font-medium text-gray-900">{{ getAgentName(assignment) }}</div>
                    <div class="text-xs text-gray-500">{{ getAgentEmail(assignment) }}</div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ getCouriersCount(assignment) }} {{ $t('couriers') }}</div>
                <div class="text-xs text-gray-500">
                  {{ getCourierNames(assignment.assignedCouriers).slice(0, 2).join(', ') }}
                  <span v-if="getCouriersCount(assignment) > 2">...</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="status in getTrackingStatuses(assignment).slice(0, 3)"
                    :key="status"
                    class="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700"
                  >
                    {{ status }}
                  </span>
                  <span v-if="getTrackingStatuses(assignment).length > 3" class="text-xs text-gray-500">
                    +{{ getTrackingStatuses(assignment).length - 3 }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex gap-1">
                  <span
                    v-for="country in getCountries(assignment)"
                    :key="country"
                    class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
                  >
                    {{ country }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span
                  :class="assignment.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                  class="px-2 py-1 text-xs font-medium rounded-full"
                >
                  {{ assignment.isActive ? $t('Active') : $t('Inactive') }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="text-sm text-gray-900">{{ $moment(assignment.createdAt).format('DD/MM/YYYY') }}</div>
                <div class="text-xs text-gray-500">{{ $t('By') }}: {{ getManagerName(assignment) }}</div>
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex justify-center gap-2">
                  <button
                    @click="openEditModal(assignment)"
                    class="p-2 text-blue-600 hover:bg-blue-100 rounded-full"
                    :title="$t('Edit')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                    </svg>
                  </button>
                  <button
                    @click="openScheduleModal(assignment)"
                    class="p-2 text-purple-600 hover:bg-purple-100 rounded-full"
                    :title="$t('View Schedule')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                  <button
                    @click="toggleActive(assignment)"
                    :class="assignment.isActive ? 'text-red-600 hover:bg-red-100' : 'text-green-600 hover:bg-green-100'"
                    class="p-2 rounded-full"
                    :title="assignment.isActive ? $t('Deactivate') : $t('Activate')"
                  >
                    <svg v-if="assignment.isActive" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </button>
                  <button
                    @click="deleteAssignment(assignment)"
                    class="p-2 text-red-600 hover:bg-red-100 rounded-full"
                    :title="$t('Delete')"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Assignment Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg w-full max-w-2xl max-h-screen-90 overflow-hidden">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">{{ isEditing ? $t('Edit Assignment') : $t('New Assignment') }}</h3>
          <button @click="showModal = false" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-4 overflow-y-auto" style="max-height: calc(90vh - 120px);">
          <!-- Agent Selection -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Agent') }} *</label>
            <div v-if="isEditing" class="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-700">
              {{ form.agentName || 'N/A' }}
            </div>
            <select
              v-else
              v-model="form.agent"
              class="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">{{ $t('Select an agent...') }}</option>
              <option v-for="agent in availableAgents" :key="agent._id" :value="agent._id">
                {{ agent.fullName }} ({{ agent.type }})
              </option>
            </select>
          </div>

          <!-- Tracking Statuses -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Tracking Statuses') }}</label>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="status in allStatuses"
                :key="status"
                class="flex items-center gap-1 px-3 py-1 rounded-full border cursor-pointer hover:bg-gray-50"
                :class="form.trackingStatuses.includes(status) ? 'bg-blue-100 border-blue-500' : 'border-gray-300'"
              >
                <input
                  type="checkbox"
                  :value="status"
                  v-model="form.trackingStatuses"
                  class="hidden"
                />
                <span class="text-sm">{{ status }}</span>
              </label>
            </div>
          </div>

          <!-- Countries -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Countries') }}</label>
            <div class="flex flex-wrap gap-2">
              <label
                v-for="country in availableCountries"
                :key="country.code"
                class="flex items-center gap-1 px-3 py-1 rounded-full border cursor-pointer hover:bg-gray-50"
                :class="form.countries.includes(country.code) ? 'bg-blue-100 border-blue-500' : 'border-gray-300'"
              >
                <input
                  type="checkbox"
                  :value="country.code"
                  v-model="form.countries"
                  class="hidden"
                />
                <span class="text-sm">{{ country.name }}</span>
              </label>
            </div>
          </div>

          <!-- Couriers Assignment -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Assign Couriers') }}</label>
            <div class="border rounded-lg p-4 max-h-60 overflow-y-auto">
              <div
                v-for="courier in allCouriers"
                :key="courier._id"
                class="flex items-center justify-between py-2 border-b last:border-b-0"
              >
                <div class="flex items-center gap-3">
                  <input
                    type="checkbox"
                    :checked="isCourierAssigned(courier._id)"
                    @change="toggleCourier(courier)"
                    class="w-4 h-4 rounded border-gray-300"
                  />
                  <div>
                    <div class="text-sm font-medium">{{ courier.fullName }}</div>
                    <div class="text-xs text-gray-500">{{ courier.phone }}</div>
                  </div>
                </div>
                <div v-if="isCourierAssigned(courier._id)" class="flex gap-1">
                  <button
                    v-for="day in dayButtons"
                    :key="day.value"
                    type="button"
                    @click="toggleCourierDay(courier._id, day.value)"
                    :class="getCourierSchedule(courier._id).includes(day.value) ? 'bg-blue-600 text-white' : 'bg-gray-200'"
                    class="w-8 h-8 rounded text-xs font-medium"
                    :title="day.label"
                  >
                    {{ day.short }}
                  </button>
                </div>
              </div>
              <div v-if="allCouriers.length === 0" class="text-center text-gray-500 py-4">
                {{ $t('No couriers available') }}
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Notes') }}</label>
            <textarea
              v-model="form.notes"
              rows="2"
              class="w-full px-3 py-2 border rounded-lg"
              :placeholder="$t('Optional notes about this assignment...')"
            ></textarea>
          </div>
        </div>
        <div class="p-4 border-t flex justify-end gap-3">
          <button
            type="button"
            @click="showModal = false"
            class="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            @click="saveAssignment"
            :disabled="!form.agent"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {{ isEditing ? $t('Update') : $t('Create') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Schedule Modal -->
    <div v-if="showScheduleModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg w-full max-w-3xl max-h-screen-90 overflow-hidden">
        <div class="p-4 border-b flex justify-between items-center">
          <h3 class="text-lg font-semibold">{{ $t('Weekly Schedule') }} - {{ getSelectedAgentName() }}</h3>
          <button @click="showScheduleModal = false" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-4 overflow-y-auto" style="max-height: calc(90vh - 80px);">
          <div class="grid grid-cols-7 gap-2">
            <div v-for="day in dayButtons" :key="day.value" class="text-center">
              <div class="font-medium text-gray-700 mb-2 py-2 bg-gray-100 rounded">{{ day.label }}</div>
              <div class="space-y-1">
                <div
                  v-for="courier in getCouriersForDay(day.value)"
                  :key="getCourierId(courier)"
                  class="text-xs bg-blue-100 text-blue-800 rounded px-2 py-1"
                >
                  {{ getCourierDisplayName(courier) }}
                </div>
                <div v-if="getCouriersForDay(day.value).length === 0" class="text-xs text-gray-400 py-2">
                  -
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AgentAssignments',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      assignments: [],
      availableAgents: [],
      allCouriers: [],
      availableCountries: [],
      showModal: false,
      showScheduleModal: false,
      isEditing: false,
      selectedAssignment: null,
      form: {
        agent: '',
        trackingStatuses: ['shipped', 'cancelled', 'unreachable', 'return'],
        countries: [],
        assignedCouriers: [],
        notes: ''
      },
      allStatuses: ['shipped', 'cancelled', 'refused', 'unreachable', 'pending return', 'return', 'remind', 'reprogrammer'],
      dayButtons: [
        { value: 0, label: 'Sunday', short: 'Su' },
        { value: 1, label: 'Monday', short: 'Mo' },
        { value: 2, label: 'Tuesday', short: 'Tu' },
        { value: 3, label: 'Wednesday', short: 'We' },
        { value: 4, label: 'Thursday', short: 'Th' },
        { value: 5, label: 'Friday', short: 'Fr' },
        { value: 6, label: 'Saturday', short: 'Sa' }
      ]
    };
  },
  computed: {
    activeCount() {
      return this.assignments.filter(a => a.isActive).length;
    },
    totalCouriers() {
      return this.assignments.reduce((sum, a) => {
        return sum + (a.assignedCouriers ? a.assignedCouriers.length : 0);
      }, 0);
    }
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await this.loadCountries();
      await this.loadAssignments();
      await this.loadAvailableAgents();
      await this.loadCouriers();
    }
  },
  methods: {
    async loadCountries() {
      try {
        var response = await this.$server.search('warehouses', { all: true, limit: 100 });
        if (response && response.content && response.content.results) {
          var countriesMap = {};
          response.content.results.forEach(function(warehouse) {
            if (warehouse.country) {
              countriesMap[warehouse.country] = {
                code: warehouse.country,
                name: warehouse.countryName || warehouse.name || warehouse.country
              };
            }
          });
          this.availableCountries = Object.values(countriesMap).sort(function(a, b) {
            return a.name.localeCompare(b.name);
          });
        }
      } catch (error) {
        console.error('Failed to load countries:', error);
        this.availableCountries = [];
      }
    },

    // Safe accessor methods
    getAgentName(assignment) {
      return assignment && assignment.agent ? assignment.agent.fullName : '';
    },
    getAgentEmail(assignment) {
      return assignment && assignment.agent ? assignment.agent.email : '';
    },
    getAgentId(assignment) {
      return assignment && assignment.agent ? assignment.agent._id : '';
    },
    getManagerName(assignment) {
      return assignment && assignment.manager ? assignment.manager.fullName : '';
    },
    getCouriersCount(assignment) {
      return assignment && assignment.assignedCouriers ? assignment.assignedCouriers.length : 0;
    },
    getTrackingStatuses(assignment) {
      return assignment && assignment.trackingStatuses ? assignment.trackingStatuses : [];
    },
    getCountries(assignment) {
      return assignment && assignment.countries ? assignment.countries : [];
    },
    getSelectedAgentName() {
      if (this.selectedAssignment && this.selectedAssignment.agent) {
        return this.selectedAssignment.agent.fullName;
      }
      return '';
    },
    getCourierId(courier) {
      if (courier && courier.courier) {
        return courier.courier._id || courier.courier;
      }
      return '';
    },
    getCourierDisplayName(courier) {
      if (courier.courierName) return courier.courierName;
      if (courier.courier && courier.courier.fullName) return courier.courier.fullName;
      return 'Unknown';
    },

    async loadAssignments() {
      this.loading = true;
      try {
        const res = await this.$server.getAssignments('agentAssignments', {});
        if (res && res.content && res.content.results) {
          this.assignments = res.content.results;
        } else if (res && res.results) {
          this.assignments = res.results;
        }
      } catch (error) {
        console.error('Error loading assignments:', error);
      }
      this.loading = false;
    },

    async loadAvailableAgents() {
      try {
        const res = await this.$server.getAvailableAgents('agentAssignments', {});
        if (res && res.content) {
          this.availableAgents = res.content;
        } else if (res && Array.isArray(res)) {
          this.availableAgents = res;
        }
      } catch (error) {
        console.error('Error loading agents:', error);
      }
    },

    async loadCouriers() {
      try {
        const res = await this.$server.getCouriers('agentAssignments', {});
        if (res && res.content) {
          this.allCouriers = res.content;
        } else if (res && Array.isArray(res)) {
          this.allCouriers = res;
        }
      } catch (error) {
        console.error('Error loading couriers:', error);
      }
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
    },

    getCourierNames(couriers) {
      if (!couriers) return [];
      var self = this;
      return couriers.map(function(c) {
        if (c.courierName) return c.courierName;
        if (c.courier && c.courier.fullName) return c.courier.fullName;
        return 'Unknown';
      });
    },

    openCreateModal() {
      this.isEditing = false;
      this.form = {
        agent: '',
        trackingStatuses: ['shipped', 'cancelled', 'unreachable', 'return'],
        countries: this.idWharhouse && this.idWharhouse.country ? [this.idWharhouse.country] : [],
        assignedCouriers: [],
        notes: ''
      };
      this.showModal = true;
    },

    openEditModal(assignment) {
      this.isEditing = true;
      this.selectedAssignment = assignment;
      this.form = {
        _id: assignment._id,
        agent: assignment.agent ? assignment.agent._id : '',
        agentName: assignment.agent ? assignment.agent.fullName : '',
        trackingStatuses: assignment.trackingStatuses || [],
        countries: assignment.countries || [],
        assignedCouriers: (assignment.assignedCouriers || []).map(function(c) {
          return {
            courier: c.courier ? (c.courier._id || c.courier) : '',
            courierName: c.courierName || (c.courier ? c.courier.fullName : ''),
            scheduleDays: c.scheduleDays || [],
            isActive: c.isActive !== false
          };
        }),
        notes: assignment.notes || ''
      };
      this.showModal = true;
    },

    openScheduleModal(assignment) {
      this.selectedAssignment = assignment;
      this.showScheduleModal = true;
    },

    isCourierAssigned(courierId) {
      return this.form.assignedCouriers.some(function(c) { return c.courier === courierId; });
    },

    getCourierSchedule(courierId) {
      var courier = this.form.assignedCouriers.find(function(c) { return c.courier === courierId; });
      return courier && courier.scheduleDays ? courier.scheduleDays : [];
    },

    toggleCourier(courier) {
      var index = this.form.assignedCouriers.findIndex(function(c) { return c.courier === courier._id; });
      if (index > -1) {
        this.form.assignedCouriers.splice(index, 1);
      } else {
        this.form.assignedCouriers.push({
          courier: courier._id,
          courierName: courier.fullName,
          scheduleDays: [1, 2, 3, 4, 5],
          isActive: true
        });
      }
    },

    toggleCourierDay(courierId, day) {
      var courier = this.form.assignedCouriers.find(function(c) { return c.courier === courierId; });
      if (!courier) return;

      var index = courier.scheduleDays.indexOf(day);
      if (index > -1) {
        courier.scheduleDays.splice(index, 1);
      } else {
        courier.scheduleDays.push(day);
        courier.scheduleDays.sort();
      }
    },

    getCouriersForDay(day) {
      if (!this.selectedAssignment || !this.selectedAssignment.assignedCouriers) return [];
      return this.selectedAssignment.assignedCouriers.filter(function(c) {
        return c.isActive && c.scheduleDays && c.scheduleDays.includes(day);
      });
    },

    async saveAssignment() {
      try {
        var data = {
          agent: this.form.agent,
          trackingStatuses: this.form.trackingStatuses,
          countries: this.form.countries,
          assignedCouriers: this.form.assignedCouriers,
          notes: this.form.notes
        };

        var res;
        if (this.isEditing) {
          data._id = this.form._id;
          res = await this.$server.updateAssignment('agentAssignments', data);
        } else {
          res = await this.$server.createAssignment('agentAssignments', data);
        }

        if (res && (res.content || res._id)) {
          alert(this.$t(this.isEditing ? 'Assignment updated successfully' : 'Assignment created successfully'), 'success');
          this.showModal = false;
          this.loadAssignments();
          this.loadAvailableAgents();
        } else {
          alert(res || this.$t('Error saving assignment'));
        }
      } catch (error) {
        console.error('Error saving assignment:', error);
        alert(this.$t('Error saving assignment'));
      }
    },

    async toggleActive(assignment) {
      try {
        var res = await this.$server.updateAssignment('agentAssignments', {
          _id: assignment._id,
          isActive: !assignment.isActive
        });

        if (res && (res.content || res._id)) {
          this.loadAssignments();
        }
      } catch (error) {
        console.error('Error toggling assignment:', error);
      }
    },

    async deleteAssignment(assignment) {
      var agentName = this.getAgentName(assignment);
      if (!confirm(this.$t('Are you sure you want to delete the assignment for') + ' ' + agentName + '?')) {
        return;
      }

      try {
        var res = await this.$server.deleteAssignment('agentAssignments', { _id: assignment._id });

        if (res && (res.content || res.message || res._id)) {
          alert(this.$t('Assignment deleted successfully'), 'success');
          this.loadAssignments();
          this.loadAvailableAgents();
        } else {
          alert(res || this.$t('Error deleting assignment'));
        }
      } catch (error) {
        console.error('Error deleting assignment:', error);
        alert(this.$t('Error deleting assignment'));
      }
    }
  }
};
</script>

<style scoped>
.max-h-screen-90 {
  max-height: 90vh;
}
</style>
